import { useEffect, useRef } from "react";

/** 끌어서 둘러보기 — 가로 1px 당 도는 각(도) · 세로 1px 당 높이 배율 */
export const ORBIT_DEG_PER_PX = 0.4;
export const ORBIT_TILT_PER_PX = 0.006;

export interface OrbitableView {
  setOrbit?(yawDeg: number, tilt: number): void;
}

/** 끈 거리(px) → 둘러보기 값(도 · 높이 배율 − 1). 오른쪽으로 끌면 기계가 오른쪽으로 돈다(카메라는 왼쪽으로) · 아래로 끌면 위에서 내려다본다 */
export function orbitFrom(start: { yaw: number; tilt: number }, dx: number, dy: number) {
  return { yaw: start.yaw - dx * ORBIT_DEG_PER_PX, tilt: start.tilt + dy * ORBIT_TILT_PER_PX };
}

/**
 * 3D 뷰 둘러보기(2026-10-07 요청 — 기구 무대) — 캔버스를 마우스 · 손가락으로 끌면 기계 둘레를 돌고, 두 번 누르면 처음 자리로.
 * 뷰는 getView 로 그때그때 받는다(ZoomControls 와 같다). 값은 이 화면에만 — 저장하지 않는다. 높이 범위는 렌더러 setOrbit 이 자른다.
 */
export function useOrbitDrag(
  target: React.RefObject<HTMLElement | null>,
  getView: () => OrbitableView | null,
  enabled: boolean,
) {
  const getRef = useRef(getView);
  getRef.current = getView;
  useEffect(() => {
    const el = target.current;
    if (!el || !enabled) return;
    let orbit = { yaw: 0, tilt: 0 };
    let drag: { id: number; x: number; y: number; from: typeof orbit } | null = null;
    const apply = (o: typeof orbit) => {
      orbit = { yaw: o.yaw, tilt: Math.min(1.5, Math.max(-0.7, o.tilt)) };
      getRef.current()?.setOrbit?.(orbit.yaw, orbit.tilt);
    };
    const down = (e: PointerEvent) => {
      if (drag || (e.pointerType === "mouse" && e.button !== 0)) return;
      drag = { id: e.pointerId, x: e.clientX, y: e.clientY, from: orbit };
      el.setPointerCapture?.(e.pointerId);
    };
    const move = (e: PointerEvent) => {
      if (!drag || e.pointerId !== drag.id) return;
      apply(orbitFrom(drag.from, e.clientX - drag.x, e.clientY - drag.y));
    };
    const up = (e: PointerEvent) => {
      if (drag && e.pointerId === drag.id) drag = null;
    };
    const reset = () => apply({ yaw: 0, tilt: 0 });
    const prevTouch = el.style.touchAction;
    el.style.touchAction = "none";
    el.addEventListener("pointerdown", down);
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", up);
    el.addEventListener("dblclick", reset);
    return () => {
      el.style.touchAction = prevTouch;
      el.removeEventListener("pointerdown", down);
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("pointercancel", up);
      el.removeEventListener("dblclick", reset);
    };
  }, [target, enabled]);
}
